import * as React from 'react';
import AppBar from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import CssBaseline from '@mui/material/CssBaseline';
import Drawer from '@mui/material/Drawer';
import IconButton from '@mui/material/IconButton';
import {MenuIcon} from '../ui/icons';
import Toolbar from '@mui/material/Toolbar';
import {BrowserHistoryModule, RoutingModule} from "@intuitionrobotics/thunderstorm/frontend";
import {NavLink} from "react-router-dom";
import {StylesConfig} from "react-select";
import "./drawer-style.css";
import {
    Route_Activation,
    Route_ActivationsList,
    Route_Admin,
    Route_Home,
    Route_Monitoring,
    Route_PM,
    Route_Commands,
    Route_TTS,
    Route_UnitMonitoring
} from "./routes";
import {LoginModule} from "@modules/LoginModule";
import {EnvironmentModule} from "@modules/EnvironmentModule";
import {RenderSelectUnit} from "./renderers/Renderer_renderSelectUnit";

// e.g. "dev · v2.6.5" — clearer than the old "DEV-prod-2.6.5" env-appEnv-version.
// Computed at render (not module load): getEnvName() reads config that is only
// set after Thunder.build().
const getAppVersionLabel = () => `${(EnvironmentModule.getEnvName() || "").toLowerCase()} · v${process.env.appVersion}`;

interface Props {
    children: React.ReactNode;
    showUnitSelection: boolean;
}

const drawerWidth = 280;

// Modern, rounded look for the drawer's unit search (react-select), matched to
// the drawer's accent so it reads as part of the nav rather than a raw control.
const unitSelectStyles: StylesConfig<{ value: string, label: string }, false> = {
    control: (base, state) => ({
        ...base,
        minHeight: 44,
        borderRadius: 10,
        borderColor: state.isFocused ? "#fb7d02" : "#e7e9ee",
        boxShadow: "none",
        ":hover": {borderColor: "#fb7d02"}
    }),
    placeholder: (base) => ({...base, color: "#9aa3ad", fontSize: 14}),
    input: (base) => ({...base, fontSize: 14}),
    singleValue: (base) => ({...base, fontSize: 14}),
    menu: (base) => ({...base, borderRadius: 10, overflow: "hidden", zIndex: 20}),
    option: (base, state) => ({
        ...base,
        fontSize: 14,
        backgroundColor: state.isSelected ? "#fb7d02" : state.isFocused ? "#fff2e2" : "#fff",
        color: state.isSelected ? "#fff" : "#1c2733",
        ":active": {backgroundColor: "#fde3c6"}
    })
};
const PageLinkKeys = [
    Route_Home,
    Route_Monitoring,
    Route_Activation,
    Route_ActivationsList,
    Route_PM,
    Route_Commands,
    Route_TTS,
    Route_Admin
];

function DrawerAppBar(props: Props) {
    const [mobileOpen, setMobileOpen] = React.useState(false);

    const handleDrawerToggle = () => {
        setMobileOpen((prevState) => !prevState);
    };

    const activeRouteKey = [...PageLinkKeys].reverse().find((key) => {
        if (!RoutingModule.isRouteRegistered(key))
            return;

        const route = RoutingModule.getRoute(key);
        if (!route.label)
            return;

        if (window.location.pathname === route.path)
            return key;
    })

    const closeDrawer = () => setMobileOpen(false);
    const appVersionLabel = getAppVersionLabel();

    const drawer = (
        <Box className="mnav">
            <div className="mnav-header">
                <span className="mnav-title">IR-Q Support</span>
                <button className="mnav-back" onClick={() => {
                    closeDrawer();
                    BrowserHistoryModule.getHistory().goBack();
                }}>
                    ‹ Back
                </button>
            </div>

            <div className="mnav-section">
                <span className="mnav-label">Jump to a unit</span>
                <RenderSelectUnit
                    product={'elliq'}
                    placeholder={"Search unit id…"}
                    styles={unitSelectStyles}
                    onUnitSelected={(value: string) => {
                        closeDrawer();
                        RoutingModule.goToRoute(Route_UnitMonitoring, {product: "elliq", unitId: value});
                    }}/>
            </div>

            <nav className="mnav-links">
                {PageLinkKeys.map((key) => {
                    if (!RoutingModule.isRouteRegistered(key))
                        return;

                    const route = RoutingModule.getRoute(key);
                    if (!route.label)
                        return;

                    return <NavLink
                        key={key}
                        to={route.path}
                        onClick={closeDrawer}
                        className={`mnav-link${activeRouteKey === key ? " is-active" : ""}`}
                    >
                        {route.label}
                    </NavLink>;
                })}
            </nav>

            <div className="mnav-footer">
                <button className="mnav-logout" onClick={LoginModule.logout}>
                    Logout
                </button>
                <div className="mnav-version">{appVersionLabel}</div>
            </div>
        </Box>
    );

    return (
        <Box sx={{display: 'flex'}} className={"match_height"}>
            <CssBaseline/>
            <AppBar sx={{height: '48px', boxShadow: "none"}} component="nav" color={"transparent"}>
                <Toolbar sx={{display: 'flex', padding: {lg: "0 15px"}, justifyContent: {lg: 'center'}}}>
                    <IconButton
                        color="inherit"
                        aria-label="open drawer"
                        edge="start"
                        onClick={handleDrawerToggle}
                        sx={{mr: 2, display: {lg: 'none'}}}
                    >
                        <MenuIcon/>
                    </IconButton>
                    <Toolbar sx={{display: {xs: "none", lg: 'flex'}, padding: {lg: 0}, width: '100%'}}>
                        <div className="tnav">
                            <div className="tnav-left">
                                <button className="tnav-back" onClick={() => BrowserHistoryModule.getHistory().goBack()}>
                                    ‹ Back
                                </button>
                                <div className="tnav-unit">
                                    <RenderSelectUnit
                                        product={'elliq'}
                                        placeholder={"Search unit id…"}
                                        styles={unitSelectStyles}
                                        onUnitSelected={(value: string) => {
                                            RoutingModule.goToRoute(Route_UnitMonitoring, {product: "elliq", unitId: value});
                                        }}/>
                                </div>
                            </div>

                            <nav className="tnav-links">
                                {PageLinkKeys.map((key) => {
                                    if (!RoutingModule.isRouteRegistered(key))
                                        return;

                                    const route = RoutingModule.getRoute(key);
                                    if (!route.label)
                                        return;

                                    return <NavLink
                                        key={key}
                                        to={route.path}
                                        className={`tnav-link${activeRouteKey === key ? " is-active" : ""}`}
                                    >
                                        {route.label}
                                    </NavLink>;
                                })}
                            </nav>

                            <span className="tnav-version">{appVersionLabel}</span>
                            <button className="tnav-logout" onClick={LoginModule.logout}>
                                Logout
                            </button>
                        </div>
                    </Toolbar>
                </Toolbar>
            </AppBar>
            <nav>
                <Drawer
                    variant="temporary"
                    open={mobileOpen}
                    onClose={handleDrawerToggle}
                    ModalProps={{
                        keepMounted: true // Better open performance on mobile.
                    }}
                    sx={{
                        display: {xs: 'block', lg: 'none'},
                        '& .MuiDrawer-paper': {boxSizing: 'border-box', width: drawerWidth}
                    }}
                >
                    {drawer}
                </Drawer>
            </nav>
            <Box component="main" className={"match_width match_height flex-column"} sx={{p: 0}}>
                <Toolbar/>
                {props.children}
            </Box>
        </Box>
    );
}

export default DrawerAppBar;
